import { i18n } from '@lingui/core';
import { I18nProvider as LinguiI18nProvider, useLingui } from '@lingui/react';
import { DatesProvider, TFLoadingOverlay } from '@tf/ui';
import type React from 'react';
import { useEffect, useState } from 'react';

import { addTFConsoleMethods } from '@/core/tfConsole';

export const loadCatalog = async (locale: string) => {
	const { messages } = await import(`../../locales/${locale}.po`);
	i18n.loadAndActivate({ locale, messages });
};

const pseudoLocaleCode = 'testing';

export const useCurrentLocale = () => {
	const { i18n } = useLingui();

	return i18n.locale === pseudoLocaleCode ? 'en' : i18n.locale;
};

export const I18nProvider: React.FC<React.PropsWithChildren<{ defaultLocale: string }>> = ({
	children,
	defaultLocale,
}) => {
	const [isInitialized, setIsInitialized] = useState(false);

	useEffect(() => {
		loadCatalog(defaultLocale).then(() => {
			setIsInitialized(true);
		});

		// For testing
		let prevActiveLocale = i18n.locale;
		addTFConsoleMethods({
			l10nTogglePseudolocalization: () => {
				if (i18n.locale !== pseudoLocaleCode) {
					prevActiveLocale = i18n.locale;
					loadCatalog(pseudoLocaleCode);
				} else {
					loadCatalog(prevActiveLocale);
				}
			},
			l10nSwitchLocale: (locale: string) => {
				loadCatalog(locale);
			},
		});
	}, [defaultLocale]);

	if (!isInitialized) {
		return <TFLoadingOverlay />;
	}

	return (
		<LinguiI18nProvider i18n={i18n}>
			<DatesProvider>{children}</DatesProvider>
		</LinguiI18nProvider>
	);
};
